import ConfirmationModal from '@/components/ConfirmationModal';
import Loader from '@/components/Loader';
import ResultModal from '@/components/ResultModal';
import Constants from '@/Constants';
import { ApplicationState } from '@/store';
import { activateCategoryRequest, clearCategoryManagement, createCategoryRequest } from '@/store/categoryManagement/action';
import { openModal } from '@/store/common/actions';
import React, { useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import Modal from 'react-bootstrap/esm/Modal';
import { useDispatch, useSelector } from 'react-redux';
import { CategoryCreatedSuccessModal } from '../CategoryCreatedSuccessModal';
import './styles.scss';

type NewCategoryModalProps = {
    onCreatedSuccess: () => void;
};

type NewCategoryModalType = {
    show: () => void;
    dismiss: () => void;
};

export type FormDataType = {
    pt: string;
    en: string;
};

type LocaleType = keyof FormDataType;

const INITIAL_FORM_DATA: FormDataType = {
    pt: '',
    en: '',
};

const BaseNewCategoryModal: React.ForwardRefRenderFunction<NewCategoryModalType, NewCategoryModalProps> = (
    { onCreatedSuccess }: NewCategoryModalProps,
    ref,
) => {
    const categoryCreatedSuccessModalRef = useRef<CategoryCreatedSuccessModal>(null);

    const [shownModal, setShownModal] = useState(false);
    const [operation, setOperation] = useState('');
    const [formData, setFormData] = useState<FormDataType>(INITIAL_FORM_DATA);

    const dispatch = useDispatch();

    const { strings, loading, error, createdSuccess, organizationId, success } = useSelector((state: ApplicationState) => ({
        strings: state.locale.data,
        loading: state.categoryManagement.loading,
        error: state.categoryManagement.error,
        createdSuccess: state.categoryManagement.success,
        organizationId: state.appOrganization.data.organization.id,
        success: state.categoryManagement.success,
    }));

    const isFormValid = useMemo(() => {
        return Object.values(formData).reduce((acc, value) => acc && !!value, true);
    }, [formData]);

    const isCategoryFoundError = useMemo(() => {
        if (!error) {
            return false;
        }
        return error.response?.status === Constants.statusCode.ENTITY_ALREADY_EXISTS;
    }, [error]);

    const reset = () => {
        setFormData(INITIAL_FORM_DATA);
        dispatch(clearCategoryManagement());
    };

    const show = useCallback(() => {
        setShownModal(true);
    }, []);

    const dismiss = useCallback(() => {
        setShownModal(false);
    }, []);

    const onTextChange = (inputName: LocaleType, value: string) => {
        setFormData((prev) => ({ ...prev, [inputName]: value }));
    };

    const handleSubmit = (event: React.FormEvent) => {
        event.preventDefault();
        if (!isFormValid) {
            return;
        }

        dispatch(
            createCategoryRequest({
                applicationId: Constants.APP_ID,
                organizationId,
                locale: 'pt',
                name: formData.pt,
                activated: true,
                categoryTranslations: [
                    {
                        locale: 'en',
                        name: formData.en,
                    },
                ],
            }),
        );
    };

    useImperativeHandle(ref, () => ({
        show,
        dismiss,
    }));

    const confirmActivateCategory = () => {
        dispatch(activateCategoryRequest({ id: error?.response?.data?.id || 0 }));
        setOperation('activate');
    };

    useEffect(() => {
        //Definição da mensagem do modal
        let prefix = '';
        let sufix = '';
        if (success) {
            prefix = operation == 'activate' ? strings.ACTIVATE_SUCCESS_PREFIX : strings.INACTIVATE_SUCCESS_PREFIX;
            sufix = operation == 'activate' ? strings.ACTIVATE_SUCCESS_SUFIX : strings.INACTIVATE_SUCCESS_SUFIX;
        } else {
            prefix = operation == 'activate' ? strings.ACTIVATE_FAILURE_PREFIX : strings.INACTIVATE_FAILURE_PREFIX;
            sufix = operation == 'activate' ? strings.ACTIVATE_FAILURE_SUFIX : strings.INACTIVATE_FAILURE_SUFIX;
        }

        if (!!operation) {
            dispatch(
                openModal({
                    id: Constants.modal.ACTIVATE_INACTIVATE_RESULT_MODAL,
                    layoutModal: (
                        <ResultModal
                            description={
                                <span>
                                    <span>{prefix + ' '}</span>
                                    <span>{error?.response?.data?.name}</span>
                                    <span>{' ' + sufix}</span>
                                </span>
                            }
                            loading={loading}
                            success={success}
                        />
                    ),
                    isVisible: true,
                    isCloseAllowed: true,
                }),
            );
            if (success && !loading) {
                onCreatedSuccess();
                setOperation('');
            }
        }
    }, [success, operation, onCreatedSuccess, loading]);

    useEffect(() => {
        if (createdSuccess && shownModal && isFormValid) {
            dismiss();
            onCreatedSuccess();
            categoryCreatedSuccessModalRef.current?.show(formData);
        }
    }, [createdSuccess, onCreatedSuccess, dismiss, formData, shownModal]);

    useEffect(() => {
        if (!error || !error?.response || !isCategoryFoundError) {
            return;
        }

        const { data: categoryFound } = error.response;

        if (!categoryFound || categoryFound?.activated) {
            return;
        }

        dismiss();

        dispatch(
            openModal({
                id: Constants.modal.CONFIRMATION_MODAL,
                layoutModal: (
                    <ConfirmationModal
                        description={<span>{strings.REACTIVATE_CATEGORY_MSG}</span>}
                        onConfirm={confirmActivateCategory}
                        title={strings.REACTIVATE_CATEGORY_TITLE}
                    />
                ),
                isVisible: true,
                isCloseAllowed: true,
            }),
        );
    }, [error, dismiss, isCategoryFoundError]);

    return (
        <>
            <Modal backdrop centered onEnter={reset} onExited={reset} onHide={dismiss} show={shownModal}>
                <div className="new-category-modal-container">
                    <div className="header">
                        <h1>{strings.CATEGORY_MANAGEMENT_ADD_NEW_CATEGORY}</h1>
                        <i
                            className="material-icons-outlined align-middle close-icon"
                            data-dismiss="modal"
                            onClick={dismiss}
                            onKeyDown={() => {}}
                            role="button"
                            tabIndex={0}
                        >
                            {Constants.icons.CLOSE}
                        </i>
                    </div>

                    <form action="submit" className="form-content-container" onSubmit={handleSubmit}>
                        <div className="input-section">
                            <div className="title-container">
                                <h1>{strings.CATEGORY_PT_BR_LABEL}*</h1>
                            </div>
                            <input
                                className={`form-control ${error && isCategoryFoundError && 'has-error'}`}
                                onChange={(e) => {
                                    onTextChange('pt', e.target.value);
                                }}
                                placeholder={strings.CATEGORY_MANAGEMENT_INSERT_CATEGORY_NAME_PT}
                                type="text"
                                value={formData.pt}
                            />
                        </div>

                        <div className="input-section">
                            <div className="title-container">
                                <h1>{strings.CATEGORY_ING_LABEL}*</h1>
                            </div>
                            <input
                                className={`form-control ${error && isCategoryFoundError && 'has-error'}`}
                                onChange={(e) => {
                                    onTextChange('en', e.target.value);
                                }}
                                placeholder={strings.CATEGORY_MANAGEMENT_INSERT_CATEGORY_NAME_EN}
                                type="text"
                                value={formData.en}
                            />
                        </div>

                        {!!error && error.response?.data?.activated && (
                            <div className="error-container">
                                <i className="material-icons align-middle close-icon">{Constants.icons.ALERT_ERROR}_outline</i>
                                <span>{strings.CATEGORY_MANAGEMENT_CATEGORY_FOUND_ERROR_MSG}</span>
                            </div>
                        )}

                        {!!error && !isCategoryFoundError && (
                            <div className="error-container">
                                <i className="material-icons align-middle close-icon">{Constants.icons.ALERT_ERROR}_outline</i>
                                <span>{strings.CATEGORY_MANAGEMENT_GENERIC_ERROR_MSG}</span>
                            </div>
                        )}

                        <button className="btn btn-primary create-new-category-btn" disabled={!isFormValid || loading} type="submit">
                            {loading ? <Loader color="#999" spinnerHeight={22} spinnerWidth={22} styles={{ padding: 0 }} /> : strings.CREATE_CATEGORY}
                        </button>
                    </form>
                </div>
            </Modal>
            <CategoryCreatedSuccessModal ref={categoryCreatedSuccessModalRef} />
        </>
    );
};

export type NewCategoryModal = NewCategoryModalType;
export const NewCategoryModal = React.forwardRef(BaseNewCategoryModal);
